import React, { useCallback, useEffect, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { observer } from 'mobx-react';
import { styled } from '@this/constants/themes';
import { Fetcher } from '@this/src/util';
import type Invoice from '@this/src/domain/invoice/invoice2';
import type InvoiceCustomFormat from '@this/src/domain/invoice_custom_format/invoice_custom_format';
import { Button } from '@this/shared/ui/inputs/button';
import Notification from '@this/src/notification';
import InvoiceCustomCsv from '@this/src/domain/invoice_custom_csv/invoice_custom_csv';
import type { InvoiceCustomCsvArgs } from '@this/src/domain/invoice_custom_csv/invoice_custom_csv';
import { InvoiceSplittedTableRow } from './invoice_splitted_table_row';

interface Props {
  invoice: Invoice;
  invoiceCustomFormats: InvoiceCustomFormat[];
  encoding: string;
  setShowExternalInvoice: Dispatch<SetStateAction<Invoice | null>>;
}

interface Response {
  csvs: InvoiceCustomCsvArgs[];
}

const getCustomCsvErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message) {
    if ('response' in error) {
      const response = error.response;
      if (response && typeof response === 'object' && 'data' in response) {
        const data = response.data;
        if (data && typeof data === 'object' && 'error' in data) {
          const message = data.error;
          if (typeof message === 'string' && message.length > 0) {
            return message;
          }
        }
      }
    }

    return error.message;
  }

  if (error && typeof error === 'object' && 'responseJSON' in error) {
    const responseJSON = error.responseJSON;
    if (responseJSON && typeof responseJSON === 'object' && 'error' in responseJSON) {
      const message = responseJSON.error;
      if (typeof message === 'string' && message.length > 0) {
        return message;
      }
    }
  }

  return 'カスタムCSVの作成に失敗しました。';
};

export const InvoiceTableRow: React.FC<Props> = observer(
  ({ invoice, invoiceCustomFormats, encoding, setShowExternalInvoice }) => {
    const [creatingCustomCsv, setCreatingCustomCsv] = useState(false);
    const [cancelingCustomCsvIds, setCancelingCustomCsvIds] = useState<number[]>([]);
    const hasOnGoingCustomCsv = invoice.invoiceCustomCsvs.some(csv => csv.onGoing);

    const updateInvoiceCustomCsvs = useCallback(
      (csvs: InvoiceCustomCsvArgs[]) => {
        invoice.invoiceCustomCsvs = csvs.map(raw => new InvoiceCustomCsv(raw));
      },
      [invoice]
    );

    useEffect(() => {
      if (!hasOnGoingCustomCsv) {
        return undefined;
      }

      const poll = () => {
        Fetcher.get<Response>(`/organization/invoices/${invoice.id}/invoice_custom_csvs.json`)
          .then(res => {
            updateInvoiceCustomCsvs(res.csvs);
          })
          .catch(() => undefined);
      };

      poll();
      const intervalId = window.setInterval(poll, 5000);

      return () => {
        window.clearInterval(intervalId);
      };
    }, [hasOnGoingCustomCsv, invoice.id, updateInvoiceCustomCsvs]);

    const invoiceCustomCsvButton = (csv: InvoiceCustomCsv) => {
      const format = invoiceCustomFormats.find(f => f.id === csv.invoiceCustomFormatId);
      if (!format) return null;
      return (
        <div key={csv.id} style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          <Button
            size="small"
            href={
              csv.onGoing
                ? ''
                : `/organization/invoices/${invoice.id}/invoice_custom_csvs/${csv.id}.csv?encoding=${encoding}`
            }
            disabled={csv.onGoing}
            loading={csv.onGoing}
            isExternal
          >
            {csv.onGoing ? '作成中' : format.name}
          </Button>
          {csv.onGoing && (
            <Button
              size="small"
              color="danger"
              onClick={() => {
                cancelCustomCsv(csv.id);
              }}
              loading={cancelingCustomCsvIds.includes(csv.id)}
            >
              中止
            </Button>
          )}
        </div>
      );
    };

    const createCustomCsv = async () => {
      const hasExistingCustomCsvs = invoice.invoiceCustomCsvs.length > 0;

      setCreatingCustomCsv(true);

      try {
        const res = await Fetcher.post<Response>(
          `/organization/invoices/${invoice.id}/invoice_custom_csvs.json`,
          {}
        );
        updateInvoiceCustomCsvs(res.csvs);
        Notification.success(
          hasExistingCustomCsvs ? 'カスタムCSVの再作成を開始しました。' : 'カスタムCSVの作成を開始しました。'
        );
      } catch (e) {
        Notification.error(getCustomCsvErrorMessage(e));
      } finally {
        setCreatingCustomCsv(false);
      }
    };

    const cancelCustomCsv = async (csvId: number) => {
      setCancelingCustomCsvIds(current => [...current, csvId]);

      try {
        const res = await Fetcher.patch<Response>(
          `/organization/invoices/${invoice.id}/invoice_custom_csvs/${csvId}/cancel.json`
        );
        updateInvoiceCustomCsvs(res.csvs);
        Notification.success('カスタムCSV作成を中止しました。');
      } catch (e) {
        Notification.error(getCustomCsvErrorMessage(e));
      } finally {
        setCancelingCustomCsvIds(current => current.filter(id => id !== csvId));
      }
    };

    return (
      <>
        <Tr key={invoice.id} noBorder={invoice.invoice_splitteds.length > 0}>
          <Td>{`${invoice.targetYear}年${invoice.targetMonth}月分`} </Td>
          <Td>{invoice.chargedAt.format('YYYY/MM/DD')}</Td>
          {invoice.showQualifiedInvoice ? (
            <Td>{utils.formatPriceWithMark(invoice.totalPriceWithTaxFromTaxes)}</Td>
          ) : (
            <Td>{utils.formatPriceWithMark(invoice.totalPriceWithTax)}</Td>
          )}
          <Td>{invoice.payAt.format('YYYY/MM/DD')}</Td>
          <Td style={{ display: 'flex' }}>
            <Button
              size="small"
              href={`/organization/invoices/${invoice.id}/invoice_detail_items.csv?encoding=${encoding}`}
              isExternal
            >
              請求明細
            </Button>
            <Button
              size="small"
              href={`/organization/invoices/${invoice.id}/invoice_trip_materials.csv?encoding=${encoding}`}
              isExternal
              style={{ marginLeft: '10px' }}
            >
              集計用データ
            </Button>
            <Button
              size="small"
              href={
                invoice.showQualifiedInvoice
                  ? `/organization/invoices/${invoice.id}/invoice_journals_qualified.csv?encoding=${encoding}`
                  : `/organization/invoices/${invoice.id}/invoice_journals.csv?encoding=${encoding}`
              }
              isExternal
              style={{ marginLeft: '10px' }}
            >
              会計用データ
            </Button>
            {invoice.showTransferJournalCsv && (
              <Button
                size="small"
                href={`/organization/invoices/${invoice.id}/invoice_transfer_journals.csv?encoding=${encoding}`}
                isExternal
                style={{ marginLeft: '10px' }}
              >
                振替用データ
              </Button>
            )}
          </Td>
          {invoiceCustomFormats.length > 0 && (
            <Td>
              {invoice.showQualifiedInvoice ? (
                <div>
                  {invoice.invoiceCustomCsvs.length > 0 ? (
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                      {invoice.invoiceCustomCsvs.map(csv => invoiceCustomCsvButton(csv))}
                      {!hasOnGoingCustomCsv && (
                        <Button
                          size="small"
                          onClick={() => {
                            createCustomCsv();
                          }}
                          loading={creatingCustomCsv}
                        >
                          CSVを再作成
                        </Button>
                      )}
                    </div>
                  ) : (
                    <Button
                      size="small"
                      onClick={() => {
                        createCustomCsv();
                      }}
                      disabled={hasOnGoingCustomCsv}
                      loading={creatingCustomCsv}
                    >
                      CSVを作成
                    </Button>
                  )}
                </div>
              ) : (
                '未対応'
              )}
            </Td>
          )}
          <Td>
            {invoice.showQualifiedInvoice ? (
              <Button
                size="small"
                href={`/organization/invoices/${invoice.id}/invoice_pdf_qualified.pdf`}
                isExternal
              >
                適格請求書
              </Button>
            ) : (
              <Button size="small" href={`/organization/invoices/${invoice.id}.pdf`} isExternal>
                請求書
              </Button>
            )}
          </Td>
          <Td>
            <Button size="small" className="button" onClick={() => setShowExternalInvoice(invoice)}>
              外部連携
            </Button>
          </Td>
        </Tr>
        {invoice.invoice_splitteds.map((splitted, i) => (
          <InvoiceSplittedTableRow
            key={splitted.id}
            invoice={invoice}
            invoiceSplitItem={splitted}
            encoding={encoding}
            last={i === invoice.invoice_splitteds.length - 1}
          />
        ))}
      </>
    );
  }
);

const Tr = styled.tr<{ noBorder?: boolean }>`
  ${props =>
    props.noBorder
      ? `
    & > td {
      border-bottom: none;
    }
  `
      : ''}
`;

const Td = styled.td`
  padding: 4px;
`;
